/**
 * Onboarding Portal & Sharing API Service
 *
 * Option A: Public share link viewer (no auth)
 * Option B: Customer contact portal (JWT auth via onboarding_portal_token)
 */

import axios from 'axios';
import { apiClient } from '../apiClient';

import { config as productConfig } from '@/src/config';
const WEAVEIN_API_URL = (productConfig.weaveinApi.baseUrl ?? 'https://weavein-api.onrender.com').trim();

// Unauthenticated client for public share links
const publicClient = axios.create({
  baseURL: WEAVEIN_API_URL,
  timeout: 30000,
  headers: { 'Content-Type': 'application/json' },
});

/* -------------------------------------------------------------------------- */
/*  Types                                                                      */
/* -------------------------------------------------------------------------- */

export interface SharedPlanView {
  type: 'plan' | 'task';
  plan: {
    id: number;
    name: string | null;
    status: string;
    health: string;
    start_date: string | null;
    target_go_live_date: string | null;
    actual_go_live_date: string | null;
    customer_org_name: string | null;
    csm_name: string | null;
    csm_email?: string | null;
  };
  progress?: { total: number; completed: number; percent: number };
  tasks?: SharedTask[];
  task?: SharedTask | null;
  notes?: SharedNote[];
  access_level: string;
}

export interface SharedTask {
  id: number;
  name: string;
  description: string | null;
  status: string;
  due_date: string | null;
  started_at: string | null;
  completed_at: string | null;
  blocker_reason: string | null;
  notes: string | null;
  sort_order: number;
  required: boolean;
  phase_id: number | null;
  milestone_id: number | null;
  phase_name: string | null;
  phase_sort?: number;
  milestone_name: string | null;
  milestone_sort?: number;
  owner_name: string | null;
  is_followup?: boolean;
  priority?: string;
}

export interface SharedNote {
  id: number;
  title: string | null;
  body: string;
  note_type: string;
  meeting_date: string | null;
  created_at: string;
  author_name: string | null;
}

export interface ShareLink {
  id: string;
  token: string;
  url: string;
  plan_id: number;
  task_id: number | null;
  label: string | null;
  access_level: string;
  has_password: boolean;
  expires_at: string | null;
  view_count: number;
  last_viewed_at: string | null;
  created_at: string;
  created_by_name?: string;
}

export interface PlanContact {
  user_id: number;
  name: string;
  email: string;
  email_verified: boolean;
  access_level: string;
  granted_at: string;
  granted_by_name: string | null;
}

export interface PortalPlan {
  id: number;
  name: string | null;
  status: string;
  health: string;
  start_date: string | null;
  target_go_live_date: string | null;
  actual_go_live_date: string | null;
  customer_org_name: string | null;
  csm_name: string | null;
  access_level: string;
  total_tasks: number;
  completed_tasks: number;
  progress_percent: number;
}

export interface PortalUser {
  id: number;
  name: string;
  email: string;
  role: string;
  cbo_id: number | null;
  org_name: string | null;
}

/* -------------------------------------------------------------------------- */
/*  Option A — Public Share Links (no auth)                                    */
/* -------------------------------------------------------------------------- */

export async function getSharedPlan(token: string, password?: string): Promise<SharedPlanView> {
  const params: Record<string, string> = {};
  if (password) params.password = password;
  const res = await publicClient.get<SharedPlanView>(`/api/shared/onboarding/${token}`, { params });
  return res.data;
}

export async function verifySharePassword(token: string, password: string): Promise<{ verified: boolean }> {
  const res = await publicClient.post(`/api/shared/onboarding/${token}/verify-password`, { password });
  return res.data;
}

export async function sharedUpdateTask(token: string, taskId: number, data: {
  status?: string; notes?: string; name?: string; description?: string;
  due_date?: string; blocker_reason?: string;
}) {
  const res = await publicClient.patch(`/api/shared/onboarding/${token}/tasks/${taskId}`, data);
  return res.data;
}

export async function sharedAddTask(token: string, data: {
  name: string; description?: string; due_date?: string; priority?: string;
}) {
  const res = await publicClient.post(`/api/shared/onboarding/${token}/tasks`, data);
  return res.data;
}

export async function sharedListDocuments(token: string) {
  const res = await publicClient.get<{ documents: any[] }>(`/api/shared/onboarding/${token}/documents`);
  return res.data.documents;
}

export async function sharedAddDocument(token: string, data: {
  document_name: string; file_url: string; document_type?: string;
  description?: string; task_id?: number;
}) {
  const res = await publicClient.post(`/api/shared/onboarding/${token}/documents`, data);
  return res.data;
}

/* -------------------------------------------------------------------------- */
/*  Option A — Admin: manage share links (authenticated)                       */
/* -------------------------------------------------------------------------- */

const SHARE_BASE = '/api/platform/v1/support/onboarding';

export async function createShareLink(planId: number, data: {
  task_id?: number;
  label?: string;
  access_level?: string;
  password?: string;
  expires_in_days?: number;
}): Promise<ShareLink> {
  const res = await apiClient.post<ShareLink>(`${SHARE_BASE}/plans/${planId}/share`, data);
  return res.data;
}

export async function listShareLinks(planId: number): Promise<ShareLink[]> {
  const res = await apiClient.get<{ share_links: ShareLink[] }>(`${SHARE_BASE}/plans/${planId}/share-links`);
  return res.data.share_links;
}

export async function revokeShareLink(token: string): Promise<void> {
  await apiClient.delete(`${SHARE_BASE}/share-links/${token}`);
}

/* -------------------------------------------------------------------------- */
/*  Option B — Admin: manage contacts (authenticated)                          */
/* -------------------------------------------------------------------------- */

export async function inviteContact(planId: number, data: {
  email: string;
  name: string;
  access_level?: string;
  plan_ids?: number[];
}): Promise<{
  user_id: number;
  email: string;
  invitation_token: string;
  invitation_url: string;
  granted_plan_ids: number[];
  access_level: string;
  expires_at: string;
}> {
  const res = await apiClient.post(`${SHARE_BASE}/plans/${planId}/contacts`, data);
  return res.data;
}

export async function listPlanContacts(planId: number): Promise<PlanContact[]> {
  const res = await apiClient.get<{ contacts: PlanContact[] }>(`${SHARE_BASE}/plans/${planId}/contacts`);
  return res.data.contacts;
}

export async function revokeContactAccess(planId: number, userId: number): Promise<void> {
  await apiClient.delete(`${SHARE_BASE}/plans/${planId}/contacts/${userId}`);
}

/* -------------------------------------------------------------------------- */
/*  Option B — Customer Contact Portal (portal auth)                           */
/* -------------------------------------------------------------------------- */

const PORTAL_BASE = '/api/customer-support/onboarding/portal';
const TOKEN_KEY = 'onboarding_portal_token';

// Create a portal-specific client that uses the portal token
function getPortalHeaders() {
  const token = localStorage.getItem(TOKEN_KEY);
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export const onboardingPortalService = {
  async login(email: string, password: string) {
    const res = await publicClient.post(`${WEAVEIN_API_URL ? '' : ''}${PORTAL_BASE}/login`, { email, password });
    if (res.data.access_token) {
      localStorage.setItem(TOKEN_KEY, res.data.access_token);
    }
    return res.data;
  },

  async acceptInvite(token: string, password: string) {
    const res = await publicClient.post(`${PORTAL_BASE}/accept-invite`, { token, password });
    if (res.data.access_token) {
      localStorage.setItem(TOKEN_KEY, res.data.access_token);
    }
    return res.data;
  },

  async getPlans(): Promise<PortalPlan[]> {
    const res = await publicClient.get<{ plans: PortalPlan[] }>(`${PORTAL_BASE}/plans`, {
      headers: getPortalHeaders(),
    });
    return res.data.plans;
  },

  async getPlanDetail(planId: number): Promise<SharedPlanView> {
    const res = await publicClient.get<SharedPlanView>(`${PORTAL_BASE}/plans/${planId}`, {
      headers: getPortalHeaders(),
    });
    return res.data;
  },

  async getTask(planId: number, taskId: number): Promise<SharedTask> {
    const res = await publicClient.get<SharedTask>(`${PORTAL_BASE}/plans/${planId}/tasks/${taskId}`, {
      headers: getPortalHeaders(),
    });
    return res.data;
  },

  async updateTask(planId: number, taskId: number, data: {
    status?: string; notes?: string; name?: string; description?: string;
    due_date?: string; blocker_reason?: string;
  }) {
    const res = await publicClient.patch(`${PORTAL_BASE}/plans/${planId}/tasks/${taskId}`, data, {
      headers: getPortalHeaders(),
    });
    return res.data;
  },

  async addTask(planId: number, data: {
    name: string; description?: string; phase_id?: number;
    due_date?: string; priority?: string;
  }) {
    const res = await publicClient.post(`${PORTAL_BASE}/plans/${planId}/tasks`, data, {
      headers: getPortalHeaders(),
    });
    return res.data;
  },

  async listDocuments(planId: number) {
    const res = await publicClient.get<{ documents: any[] }>(`${PORTAL_BASE}/plans/${planId}/documents`, {
      headers: getPortalHeaders(),
    });
    return res.data.documents;
  },

  async addDocument(planId: number, data: {
    document_name: string; file_url: string; document_type?: string;
    description?: string; file_size_bytes?: number; file_mime_type?: string;
    task_id?: number;
  }) {
    const res = await publicClient.post(`${PORTAL_BASE}/plans/${planId}/documents`, data, {
      headers: getPortalHeaders(),
    });
    return res.data;
  },

  async updateDocument(planId: number, docId: string, data: {
    document_name?: string; file_url?: string; document_type?: string;
    description?: string; task_id?: number | null;
  }) {
    const res = await publicClient.put(`${PORTAL_BASE}/plans/${planId}/documents/${docId}`, data, {
      headers: getPortalHeaders(),
    });
    return res.data;
  },

  async deleteDocument(planId: number, docId: string) {
    const res = await publicClient.delete(`${PORTAL_BASE}/plans/${planId}/documents/${docId}`, {
      headers: getPortalHeaders(),
    });
    return res.data;
  },

  async getMe(): Promise<PortalUser> {
    const res = await publicClient.get<PortalUser>(`${PORTAL_BASE}/me`, {
      headers: getPortalHeaders(),
    });
    return res.data;
  },

  isLoggedIn(): boolean {
    return !!localStorage.getItem(TOKEN_KEY);
  },

  logout() {
    localStorage.removeItem(TOKEN_KEY);
  },

  getToken(): string | null {
    return localStorage.getItem(TOKEN_KEY);
  },
};
